<!-- src/components/NotificationDropdown.vue -->
<template>
  <!-- Modal Overlay -->
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center"
    @click="$emit('close')"
  >
    <!-- Background Overlay -->
    <div class="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm"></div>

    <!-- Modal Content -->
    <div
      class="relative bg-gray-800 rounded-lg shadow-2xl border border-gray-700 w-full max-w-md mx-4 max-h-[90vh] overflow-hidden"
      @click.stop
    >
      <!-- Header -->
      <div
        class="px-6 py-4 border-b border-gray-700 flex justify-between items-center"
      >
        <h3 class="text-xl font-semibold text-white">Obaveštenja</h3>
        <div class="flex items-center space-x-3">
          <!-- Connection status -->
          <div class="flex items-center space-x-2">
            <span
              class="w-2 h-2 rounded-full"
              :class="{
                'bg-green-500': isConnected,
                'bg-red-500': !isConnected && connectionError,
                'bg-yellow-500': !isConnected && !connectionError,
              }"
            ></span>
            <span class="text-xs text-gray-400">
              {{ isConnected ? 'Online' : 'Offline' }}
            </span>
          </div>

          <!-- Close button -->
          <button
            @click="$emit('close')"
            class="text-gray-400 hover:text-white transition-colors p-1 rounded-full hover:bg-gray-700"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              ></path>
            </svg>
          </button>
        </div>
      </div>

      <!-- Connection error message -->
      <div
        v-if="!isConnected && connectionError"
        class="px-6 py-3 bg-red-900 bg-opacity-50 border-b border-gray-700 text-sm text-red-300"
      >
        <div class="flex items-center">
          <svg class="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path
              fill-rule="evenodd"
              d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
              clip-rule="evenodd"
            />
          </svg>
          {{ connectionError }}
          <button
            @click="forceReconnect"
            class="ml-2 text-xs underline hover:no-underline"
          >
            Pokušaj ponovo
          </button>
        </div>
      </div>

      <!-- Badge Summary -->
      <div
        v-if="totalBadgeCount > 0"
        class="px-6 py-3 border-b border-gray-700 bg-gray-750"
      >
        <div class="grid grid-cols-2 gap-3 text-sm">
          <div
            v-if="badges.messages > 0"
            class="flex items-center text-blue-400"
          >
            <span class="w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
            <span>{{ badges.messages }} poruka</span>
          </div>
          <div
            v-if="badges.follows > 0"
            class="flex items-center text-green-400"
          >
            <span class="w-3 h-3 bg-green-500 rounded-full mr-2"></span>
            <span>{{ badges.follows }} praćenja</span>
          </div>
          <div
            v-if="badges.products > 0"
            class="flex items-center text-orange-400"
          >
            <span class="w-3 h-3 bg-orange-500 rounded-full mr-2"></span>
            <span>{{ badges.products }} proizvoda</span>
          </div>
          <div
            v-if="badges.ratings > 0"
            class="flex items-center text-yellow-400"
          >
            <span class="w-3 h-3 bg-yellow-500 rounded-full mr-2"></span>
            <span>{{ badges.ratings }} ocena</span>
          </div>
        </div>
      </div>

      <!-- Actions Bar -->
      <div
        v-if="notifications.length > 0"
        class="px-6 py-3 border-b border-gray-700 flex justify-between items-center bg-gray-750"
      >
        <span class="text-sm text-gray-400"
          >{{ notifications.length }} obaveštenja</span
        >
        <div class="flex space-x-2">
          <button
            v-if="unreadCount > 0"
            @click="markAllAsRead"
            class="text-sm text-blue-400 hover:text-blue-300 transition-colors px-2 py-1 rounded hover:bg-gray-700"
          >
            Označi sve kao pročitano
          </button>
          <button
            @click="clearAllNotifications"
            class="text-sm text-red-400 hover:text-red-300 transition-colors px-2 py-1 rounded hover:bg-gray-700"
          >
            Obriši sve
          </button>
        </div>
      </div>

      <!-- Notifications List -->
      <div class="max-h-96 overflow-y-auto">
        <!-- Empty state -->
        <div
          v-if="notifications.length === 0"
          class="px-6 py-12 text-center text-gray-400"
        >
          <svg
            class="w-16 h-16 mx-auto mb-4 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
            />
          </svg>
          <p class="text-lg font-medium mb-2">Nema obaveštenja</p>
          <p class="text-sm">
            Kada budete imali nova obaveštenja, pojaviće se ovde.
          </p>
        </div>

        <!-- Notification items -->
        <div class="divide-y divide-gray-700">
          <div
            v-for="notification in notifications.slice(0, 20)"
            :key="notification.id"
            @click="handleNotificationClick(notification)"
            class="notification-item px-6 py-4 hover:bg-gray-700 cursor-pointer transition-all duration-200"
            :class="{
              'bg-blue-900 bg-opacity-20 border-l-4 border-l-blue-500':
                !notification.read,
              'bg-gray-800': notification.read,
            }"
          >
            <div class="flex items-start space-x-4">
              <!-- Icon -->
              <div class="flex-shrink-0 mt-1">
                <div
                  class="w-10 h-10 rounded-full flex items-center justify-center"
                  :class="getNotificationIconClass(notification.type)"
                >
                  <span class="text-lg">{{
                    getNotificationIcon(notification.type)
                  }}</span>
                </div>
              </div>

              <!-- Content -->
              <div class="flex-1 min-w-0">
                <p class="text-sm font-medium text-white mb-1">
                  {{ getNotificationTitle(notification) }}
                </p>
                <p class="text-sm text-gray-300 mb-2 line-clamp-2">
                  {{ getNotificationMessage(notification) }}
                </p>
                <div class="flex items-center justify-between">
                  <p class="text-xs text-gray-500">
                    {{ formatTime(notification.createdAt) }}
                  </p>
                  <div class="flex items-center space-x-2">
                    <!-- Mark as read button -->
                    <button
                      v-if="!notification.read"
                      @click.stop="markAsRead(notification.id)"
                      class="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 rounded hover:bg-gray-600 transition-colors"
                    >
                      Označi kao pročitano
                    </button>
                    <!-- Remove button -->
                    <button
                      @click.stop="removeNotification(notification.id)"
                      class="text-red-400 hover:text-red-300 text-xs px-2 py-1 rounded hover:bg-gray-600 transition-colors"
                    >
                      Ukloni
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Footer -->
      <div
        v-if="notifications.length > 20"
        class="px-6 py-3 border-t border-gray-700 text-center bg-gray-750"
      >
        <p class="text-xs text-gray-400">
          Prikazano {{ Math.min(20, notifications.length) }} od
          {{ notifications.length }} obaveštenja
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useNotifications } from '@/composables/useNotifications';

interface Props {
  show: boolean;
  isSidebarOpen: boolean;
}

defineProps<Props>();

const emit = defineEmits<{
  close: [];
}>();

// Use notifications composable
const {
  notifications,
  badges,
  unreadCount,
  totalBadgeCount,
  isConnected,
  connectionError,
  markAsRead,
  markAllAsRead,
  removeNotification,
  clearAllNotifications,
  handleNotificationClick,
  formatTime,
  getNotificationTitle,
  getNotificationMessage,
  getNotificationIconClass,
  forceReconnect,
} = useNotifications();

// Get notification icon
const getNotificationIcon = (type: string) => {
  const icons = {
    new_message: '💬',
    user_follow: '👤',
    product_follow: '❤️',
    product_expired: '⏰',
    new_rating: '⭐',
  };
  return icons[type as keyof typeof icons] || '🔔';
};
</script>

<style scoped>
/* Custom scrollbar */
.max-h-96::-webkit-scrollbar {
  width: 6px;
}

.max-h-96::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 3px;
}

.max-h-96::-webkit-scrollbar-thumb {
  background: #6b7280;
  border-radius: 3px;
}

.max-h-96::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}

/* Notification item animations */
.notification-item {
  transition: all 0.2s ease;
}

.notification-item:hover {
  transform: translateX(2px);
}

/* Line clamp utility */
.line-clamp-2 {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* Animation for modal */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* Notification animation */
@keyframes slideInUp {
  from {
    transform: translateY(10px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.notification-item:first-child {
  animation: slideInUp 0.3s ease-out;
}
</style>
